// GTM loader and the sole event gateway. All guards fail closed: valid
// configuration, effective consent, an eligible entry document, an eligible
// current route, and the exact typed event contract are all required.

import {
  hasAuthCallbackState,
  sanitizePath,
  validateAnalyticsEvent,
  type AnalyticsEventName,
  type AnalyticsEventParamsMap,
  type ParameterizedAnalyticsEventName
} from './analyticsCore';
import { hasEffectiveAnalyticsConsent } from './consent';
import { waitForMeasurementCapture } from './measurement';
import { getPublicRoute, isAnalyticsEligible, isLoanOfficerProfilePath } from './publicRoutes';

export type ArmGtmResult = 'armed_now' | 'already_armed' | 'blocked';

let armed = false;
let loaded = false;
let disabled = false;
let entryBlocked: boolean | null = null;
const stateListeners = new Set<() => void>();
// Officer URLs are eligible as a class, but only the profile on screen right
// now may carry analytics, and only once it has loaded as published. The profile
// page reports that; leaving, or a missing or failed result, clears it.
let confirmedOfficerPath: string | null = null;

function normalizedPath(pathname: string): string {
  return sanitizePath(pathname).replace(/\/+$/, '') || '/';
}

export function setConfirmedOfficerPath(pathname: string | null): void {
  confirmedOfficerPath = pathname === null ? null : normalizedPath(pathname);
}

function officerRouteConfirmed(pathname: string): boolean {
  const path = normalizedPath(pathname);
  return !isLoanOfficerProfilePath(path) || confirmedOfficerPath === path;
}

/** React subscribers must observe arming, which happens AFTER consent updates. */
export function onGtmStateChange(listener: () => void): () => void {
  stateListeners.add(listener);
  return () => { stateListeners.delete(listener); };
}

function containerId(): string {
  return import.meta.env.VITE_GTM_CONTAINER_ID ?? '';
}

function isEntryBlocked(): boolean {
  if (entryBlocked === null) {
    entryBlocked =
      !isAnalyticsEligible(window.location.pathname) ||
      hasAuthCallbackState(window.location.search, window.location.hash);
  }
  return entryBlocked;
}

export function isCurrentDocumentAnalyticsEligible(): boolean {
  return !isEntryBlocked();
}

export function armGtm(): ArmGtmResult {
  if (armed && !disabled) return 'already_armed';
  if (disabled || !hasEffectiveAnalyticsConsent()) return 'blocked';

  const id = containerId();
  if (!id || !/^GTM-[A-Z0-9]+$/.test(id) || isEntryBlocked()) return 'blocked';

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.dataset.lendwiseGtm = id;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    script.addEventListener('load', () => { loaded = true; }, { once: true });
    document.head.appendChild(script);
    armed = true;
    stateListeners.forEach(listener => listener());
    return 'armed_now';
  } catch {
    return 'blocked';
  }
}

/** Stop every local emission immediately. The caller reloads the document. */
export function disarmGtm(): void {
  disabled = true;
  armed = false;
  stateListeners.forEach(listener => listener());
}

export function isArmed(): boolean {
  return armed && !disabled;
}

/** A disarmed document never re-arms; turning analytics back on needs a reload. */
export function isGtmDisabled(): boolean {
  return disabled;
}

function canEmit(): boolean {
  return isArmed() && hasEffectiveAnalyticsConsent()
    && isAnalyticsEligible(window.location.pathname)
    && officerRouteConfirmed(window.location.pathname);
}

function pushDataLayer(event: Record<string, unknown>): boolean {
  if (!canEmit()) return false;

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
    return true;
  } catch {
    // Analytics must never break the app.
    return false;
  }
}

export function track(name: 'apply_modal_open'): void;
export function track<Name extends ParameterizedAnalyticsEventName>(
  name: Name,
  params: AnalyticsEventParamsMap[Name]
): void;
export function track(name: AnalyticsEventName, params: unknown = {}): void {
  const validated = validateAnalyticsEvent(name, params);
  if (!validated) return;
  pushDataLayer({ event: validated.event, ...validated.params });
}

/** Allow consented tags a bounded flush before leaving the public document. */
export function trackRateEntryAndNavigate(
  placement: AnalyticsEventParamsMap['mortgage_rate_entry']['placement'],
  navigate: () => void,
  action: AnalyticsEventParamsMap['mortgage_rate_entry']['action'] = 'quote'
): void {
  const validated = validateAnalyticsEvent('mortgage_rate_entry', { placement, action });
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    window.clearTimeout(timeout);
    navigate();
  };
  const timeout = window.setTimeout(finish, 200);
  const tag = new Promise<void>(resolve => {
    if (!validated || !canEmit()) { resolve(); return; }
    const event = { event: validated.event, ...validated.params };
    if (!loaded) { pushDataLayer(event); resolve(); return; }
    if (!pushDataLayer({ ...event, eventCallback: resolve, eventTimeout: 200 })) resolve();
  });
  // Analytics and optional attribution share one 200ms ceiling. Intake remains
  // reachable when either endpoint is slow, blocked, or unavailable.
  void Promise.allSettled([tag, waitForMeasurementCapture()]).then(finish);
}

/**
 * Path-only manual page view. Dynamic officers require a canonical slug from
 * the successfully loaded public profile; a matching URL alone is not proof
 * of publication. The generic title keeps personal details out of analytics.
 */
export function trackPageView(pathname: string, publishedOfficerSlug?: string): boolean {
  const path = normalizedPath(pathname);
  const route = getPublicRoute(path);
  const resolvedOfficer = publishedOfficerSlug !== undefined
    && path === `/loan-officers/${publishedOfficerSlug}`
    && isLoanOfficerProfilePath(path)
    && path === window.location.pathname.replace(/\/+$/, '');
  if (!route?.analyticsEligible && !resolvedOfficer) return false;
  const pagePath = route?.path ?? path;

  return pushDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: route?.title ?? 'Loan Officer Profile | Lendwise Mortgage'
  });
}

/**
 * If an armed marketing document reaches an internal/unknown route through
 * SPA navigation, replace the whole document before anything else can emit.
 * Query/hash are preserved for auth; the fresh internal document never arms.
 */
export function enforceAnalyticsRouteBoundary(
  pathname: string,
  search: string,
  hash: string
): boolean {
  if (!isArmed() || isAnalyticsEligible(pathname)) return false;
  disarmGtm();
  window.location.replace(`${pathname}${search}${hash}`);
  return true;
}
